import { TrabalhoSituacaoFinalModel } from '@/services/academico/situacaofinal/situacao-final.service';

/**
 * Onde o acompanhamento sobrevive ao F5.
 *
 * O cálculo da ata roda no SERVIDOR e não tem nada a ver com a aba aberta. O que se perde num
 * recarregamento é só o número do trabalho e os filtros do relatório — e é exatamente isso que fica
 * aqui. Sem esta chave, quem apertasse F5 durante o cálculo ficaria sem saber que ele existe e
 * dispararia outro.
 */
const CHAVE = 'acompanhamentoAta';

/**
 * O que é guardado entre recarregamentos.
 *
 * Só o necessário para retomar: o andamento em si é relido do servidor, que é a fonte da verdade.
 * Guardar a lista de turmas aqui só criaria uma segunda versão do estado para sair de sincronia.
 */
export type AcompanhamentoAtaPersistido = {
  idTrabalho: string;
  filtrosRelatorio: string | null;
  codigoRelatorio: string;
  codigoEscola: number;
  ano: number;
  semestre: number;
  titulo: string;
  visivel: boolean;
};

export function lerPersistido(): AcompanhamentoAtaPersistido | null {
  try {
    const bruto = localStorage.getItem(CHAVE);

    if (!bruto) return null;

    const dados = JSON.parse(bruto) as AcompanhamentoAtaPersistido;

    return dados?.idTrabalho ? dados : null;
  } catch {
    // localStorage corrompido não pode derrubar a aplicação inteira na carga: sem acompanhamento
    // não se perde nada, o cálculo continua no servidor.
    return null;
  }
}

export function gravarPersistido(dados: AcompanhamentoAtaPersistido): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(dados));
  } catch {
    // Cota estourada ou modo privativo: segue sem persistir.
  }
}

export function apagarPersistido(): void {
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    // idem
  }
}

export type State = {
  /** O trabalho sendo acompanhado. Nulo quando não há nenhum. */
  idTrabalho: string | null;

  /** O último andamento lido do servidor. */
  trabalho: TrabalhoSituacaoFinalModel | null;

  /**
   * O payload do relatório, pronto para emitir.
   *
   * Fica aqui — e não na tela da ata — porque a caixa flutuante aparece em QUALQUER tela, e o
   * usuário tem de conseguir imprimir de onde estiver quando o cálculo terminar.
   */
  filtrosRelatorio: string | null;

  codigoRelatorio: string;
  codigoEscola: number;
  ano: number;
  semestre: number;

  /** O que a caixa mostra como título — normalmente o nome da ata e o recorte emitido. */
  titulo: string;

  /** A caixa está aberta? Fechar é decisão do usuário, e ela some até ele pedir de novo. */
  visivel: boolean;

  /** Recolhida: mostra só a linha de progresso, sem a lista de turmas. */
  recolhida: boolean;

  /** O que impediu o acompanhamento, quando impediu. */
  erro: string;
};

const persistido = lerPersistido();

export const state: State = {
  idTrabalho: persistido?.idTrabalho ?? null,
  trabalho: null,
  filtrosRelatorio: persistido?.filtrosRelatorio ?? null,
  codigoRelatorio: persistido?.codigoRelatorio ?? '',
  codigoEscola: persistido?.codigoEscola ?? 0,
  ano: persistido?.ano ?? 0,
  semestre: persistido?.semestre ?? 0,
  titulo: persistido?.titulo ?? 'Ata de Resultados Finais',
  visivel: persistido?.visivel ?? false,
  recolhida: false,
  erro: '',
};
